<script setup lang="ts">
/**
 * The shell every screen sits in: skip link, header, the routed screen, help footer, and the
 * two states that are not screens at all — still loading, or unable to start.
 *
 * Route changes are announced politely and focus moves to `<main>`, so someone using a screen
 * reader or a keyboard is told where they now are instead of being left at the top of a page
 * that quietly changed underneath them (START.md §9).
 *
 * `BrandProvider` renders nothing and sits here rather than in a screen because this client's
 * colours have to be on the document root before anything below it paints, and they have to stay
 * there across every route change.
 */

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';

// The Altamont masthead and the "need help?" footer. Both are owned by the chrome agent
// (`src/components/`), and both must render sensibly before the config has loaded — they are
// on screen during the boot and fatal states below, when `config.settings` is still null.
import AppHeader from '@/components/AppHeader.vue';
import BrandProvider from '@/components/BrandProvider.vue';
import HelpFooter from '@/components/HelpFooter.vue';
import { useBrandFavicon } from '@/lib/favicon';
import { useApplicationStore } from '@/stores/application';
import { useConfigStore } from '@/stores/config';

const route = useRoute();
const config = useConfigStore();
const application = useApplicationStore();

// The tab icon is this client's logo too, not just the masthead. Renderless, like BrandProvider,
// and here for the same reason: it belongs to the document, not to any one screen.
useBrandFavicon(() => config.logoUrl);

const main = ref<HTMLElement | null>(null);
const announcement = ref('');
const reduceMotion = ref(false);

let motionQuery: MediaQueryList | null = null;

function onMotionChange(event: MediaQueryListEvent | MediaQueryList): void {
  reduceMotion.value = event.matches;
}

onMounted(() => {
  if (typeof window.matchMedia !== 'function') return;
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  onMotionChange(motionQuery);
  motionQuery.addEventListener('change', onMotionChange);
});

onBeforeUnmount(() => {
  motionQuery?.removeEventListener('change', onMotionChange);
});

/** Nothing has arrived yet — show a quiet holding screen rather than an empty page. */
const booting = computed(() => !config.loaded && config.loading && !config.fatalError);

/** The config could not be fetched at all. Offer one button: try again. */
const stalled = computed(() => !config.loaded && !config.loading && !config.fatalError);

const transitionName = computed(() => (reduceMotion.value ? 'alt-page-still' : 'alt-page'));

watch(
  () => route.fullPath,
  (_to, from) => {
    const title =
      route.name === 'question' && application.progressLabel
        ? application.progressLabel
        : String(route.meta['title'] ?? '');
    announcement.value = title;
    // Not on the very first render: nobody wants focus yanked before they have read anything.
    if (from !== undefined) main.value?.focus();
  },
  { immediate: true },
);

function retry(): void {
  void config.load(true);
}

function reload(): void {
  window.location.reload();
}
</script>

<template>
  <!-- Renderless: writes this client's palette onto <html> once the config has arrived. -->
  <BrandProvider />

  <a class="alt-skip-link" href="#main">Skip to the main part of this page</a>

  <div class="alt-app">
    <AppHeader />

    <main id="main" ref="main" class="alt-app__main" tabindex="-1">
      <!-- The app cannot run: no configuration, or something failed hard during start-up. -->
      <div v-if="config.fatalError" class="alt-shell alt-shell--phone">
        <div class="alt-card alt-stack">
          <h1>We can't open your video application right now.</h1>
          <p>{{ config.fatalError }}</p>
          <button type="button" class="alt-btn alt-btn--primary alt-btn--block" @click="reload">
            Try again
          </button>
        </div>
      </div>

      <!-- Still fetching what the screens need. -->
      <div v-else-if="booting" class="alt-shell alt-shell--phone">
        <div class="alt-card alt-row alt-row--center" aria-busy="true">
          <span class="alt-spinner" aria-hidden="true"></span>
          <p class="alt-boot-note">Getting things ready…</p>
        </div>
      </div>

      <!-- We reached the end of the road without the config; one clear way forward. -->
      <div v-else-if="stalled" class="alt-shell alt-shell--phone">
        <div class="alt-card alt-stack">
          <h1>We couldn't load this page.</h1>
          <p role="alert">
            {{ config.error ?? 'Check your connection and try again.' }}
          </p>
          <button type="button" class="alt-btn alt-btn--primary alt-btn--block" @click="retry">
            Try again
          </button>
        </div>
      </div>

      <RouterView v-else v-slot="{ Component }">
        <Transition :name="transitionName" mode="out-in">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </main>

    <HelpFooter />
  </div>

  <!-- Route changes are spoken here, quietly, after the screen has settled. -->
  <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
    {{ announcement }}
  </div>
</template>

<style scoped>
.alt-app {
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  flex-direction: column;
}

.alt-app__main {
  flex: 1 1 auto;
}

/* The focus ring is the only thing that should show on <main>; it is a landmark, not a control. */
.alt-app__main:focus {
  outline: none;
}

.alt-boot-note {
  color: var(--alt-slate);
}

/* Off-screen until it is tabbed to, then the first thing on the page. */
.alt-skip-link {
  position: absolute;
  z-index: var(--alt-z-toast);
  top: calc(-1 * (var(--alt-tap) + var(--alt-space-6)));
  left: var(--alt-space-4);
  display: inline-flex;
  min-height: var(--alt-tap);
  align-items: center;
  padding: var(--alt-space-3) var(--alt-space-5);
  border-radius: var(--alt-radius);
  background-color: var(--alt-navy);
  color: var(--alt-white);
  font-size: var(--alt-text-lg);
  font-weight: 700;
  text-decoration: none;
  transition: top 0.15s ease-out;
}

.alt-skip-link:focus,
.alt-skip-link:focus-visible {
  top: var(--alt-space-4);
}

/* One screen at a time, a short cross-fade with no movement to chase. */
.alt-page-enter-active,
.alt-page-leave-active {
  transition:
    opacity 0.18s ease-out,
    transform 0.18s ease-out;
}

.alt-page-enter-from,
.alt-page-leave-to {
  opacity: 0;
  transform: translateY(var(--alt-space-2));
}

/* prefers-reduced-motion: no transition classes are styled, so screens simply swap. */
</style>
